import { useCallback, useEffect, useRef, useState } from 'react';
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper';
import { Device } from 'zebra-browser-print-wrapper/lib/types';

const INTERVAL_CHECK_PRINT = 10 * 1000;

export const useZebraPrinter = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [printIsReady, setPrintIsReady] = useState<boolean>(false);
  const [browserPrint, setZebraBrowserPrint] = useState<ZebraBrowserPrintWrapper | null>(null);
  const [printerInfos, setPrinterInfos] = useState<Device | null>(null);

  const timer = useRef<NodeJS.Timer>();

  const initializeZebra = useCallback(async () => {
    try {
      setIsLoading(true);
      setZebraBrowserPrint(new ZebraBrowserPrintWrapper());
    } catch (error) {
      console.error('Error while initializing zebra ', error);
      setError(error ? (error as Error) : new Error('Error while initializing zebra '));
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setError, setZebraBrowserPrint]);

  const checkPrinterStatus = useCallback(async () => {
    if (browserPrint) {
      try {
        const printerStatus = await browserPrint.checkPrinterStatus();
        setPrintIsReady(printerStatus.isReadyToPrint);
      } catch (e) {
        setError(new Error('check_error_print_status'));
        setPrintIsReady(false);
      }
    }
  }, [browserPrint, setPrintIsReady]);

  const preparePrint = useCallback(async () => {
    setIsLoading(true);

    if (!browserPrint) {
      return setError(new Error('cant_prepare_print'));
    }

    try {
      const defaultPrinter = await browserPrint.getDefaultPrinter();
      browserPrint.setPrinter(defaultPrinter);
      setPrinterInfos(defaultPrinter);
    } catch (e) {
      console.error('Cant get default print ', e);
      setError(new Error('cant_get_default_printer'));
    }

    await checkPrinterStatus();
    setIsLoading(false);
  }, [browserPrint, setIsLoading, setPrinterInfos, checkPrinterStatus]);

  useEffect(() => {
    if (browserPrint) {
      preparePrint();
    }
  }, [browserPrint, preparePrint]);

  useEffect(() => {
    initializeZebra();
  }, [initializeZebra]);

  const cleanTimeOutIfExist = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, []);

  const printerCheckTick = useCallback(() => {
    cleanTimeOutIfExist();
    timer.current = setInterval(checkPrinterStatus, INTERVAL_CHECK_PRINT);
  }, [checkPrinterStatus, cleanTimeOutIfExist]);

  useEffect(() => {
    printerCheckTick();
    return () => {
      cleanTimeOutIfExist();
    };
  }, [printerCheckTick, cleanTimeOutIfExist]);

  return {
    isLoading,
    printIsReady,
    error,
    printerInfos,
    browserPrint,
    checkPrinterStatus,
  };
};
